import { ICommentsAndObservations } from './global';
import { IProductDelivery, ITrackingInfo, ICountry as IOrderCountry } from './orders';

export enum STOCK_ORDER_PROPERTIES_TO_SEARCH {
  ORDER_ID = 'N° de orden',
  INVOICE_NUMBER = 'N° de factura',
  PROVIDER = 'Proveedor',
  SKU = 'SKU',
  SERIAL = 'Serial'
}

export type StockOrderPropertyToFilter = 'orderId' | 'invoiceNumber' | 'provider' | 'sku' | 'serial';

export interface StockOrderPropertyToSearch {
  name: STOCK_ORDER_PROPERTIES_TO_SEARCH;
  propertyToFilter: StockOrderPropertyToFilter;
}

export interface IStockOrderItem {
  sku: string;
  quantity: number;
  value: number;
}

export interface IStockOrder {
  countryCode: string;
  providerId: number;
  providerName: string;
  providerOrderNumber: string;
  providerInvoiceNumber?: string;
  items: IStockOrderItem[];
  status: number;
}

export interface IStockOrderData {
  orderId: number;
  uuid: string;
  country: ICountry;
  createdAt: string;
  providerName: string;
  nextDeliveryDate: string | null;
  deliveryDate: string | null;
  canceledAt: string | null;
  totalValue: string;
  currency: string;
  shipments: {
    confirmed: number;
    deliveryProcess: number;
    delayed: number;
    delivered: number;
  };
  units: number;
  stockOrderComments: ICommentsAndObservations[];
  status?: string;
}

export interface IRequesterInfo {
  name: string;
  email: string;
  photoUrl: string;
}

export interface IProviderInfo {
  name: string;
  orderNumber: string;
}

export interface IStockOrderProductItem {
  productId: number;
  serial: string;
  value: number;
  currency: string;
}

export interface ITracking {
  trackingLink: string;
  trackingCode: string;
  estimatedDeliveryDate: string;
}

export interface IStatusLog {
  id: number;
  status: string;
  createdAt: string;
}

export interface IStockOrderProduct {
  name: string;
  pills: string | null;
  sku: string;
}

export interface IStockOrderShipment {
  stockShipmentUuid: string;
  stockShipmentId: number;
  number: string;
  quantity: number;
  items: IStockOrderProductItem[];
  product: IStockOrderProduct;
  status: string;
  statusId: number;
  tracking: ITracking;
  statusLog: IStatusLog[];
  country: ICountry;
}

export interface IStockOrderProduct {
  name: string;
  brand: string;
  brandImage: string;
  pills: string | null;
  sku: string;
  quantity: number;
  items: IStockOrderProductItem[];
}

export interface IStockOrderResume {
  reference: string;
  quantity: number;
  value: number;
  subtotal: number;
  currency: string;
}

export interface IStockOrderBillingData {
  providerOrderNumber: string;
  providerInvoiceNumber: string;
  providerInvoiceUrl: string;
  totalValue: string;
  total: string;
  currency: string;
  orderResume: IStockOrderResume[];
}

export interface IStockOrderDetail {
  orderId: number;
  status: string;
  references: number;
  country: ICountry;
  generalData: {
    requesterInfo: IRequesterInfo;
    providerInfo: IProviderInfo;
    shipments: IStockOrderShipment[];
  };
  listOfProducts: IStockOrderProduct[];
  billingData: IStockOrderBillingData;
  stockOrderComments: ICommentsAndObservations[];
}

export interface IStockShipmentDeliveryProcessInfo {
  trackingCode: string;
  trackingLink: string;
  estimatedDeliveryDate: string;
  stockShipmentsIds: number[];
}

export interface IStockShipmentSerialsInfo {
  stockShipmentId: number;
  productId: number;
  serial: string;
}

export interface IStockShipmentDeliveredInfo {
  deliveryDate: string;
  serialsInfo: IStockShipmentSerialsInfo[];
}

export interface IStockOrderShipmentSerials {
  productId: number;
  serial: string;
}

export interface IStockShipmentUpdateStatus {
  stockOrderId: number;
  stockShipmentStatusId: number;
  deliveryProcessInfo?: IStockShipmentDeliveryProcessInfo;
  deliveredInfo?: IStockShipmentDeliveredInfo;
}

export interface IStockOrderBillingData {
  providerInvoiceNumber: string;
  providerOrderNumber: string;
}

export interface ICreateStockOrderResponse {
  data: {
    id: number;
  };
  status: number;
}

export interface IUpdateStockOrderBillingDataResponse {
  response: string;
  status: number;
}

export interface IStockShipmentTracking {
  stockShipmentsUuids: string[];
  trackingCode: string;
  trackingLink: string;
  estimatedDeliveryDate: string;
}

export interface IUpdateProductValues {
  productId: number;
  value: number;
}

export type IStockOrderShipmentStatusName =
  | 'Confirmada'
  | 'Proceso de entrega'
  | 'Entregada'
  | 'Atrasada'
  | 'Cancelada';

export const META_BASE_STOCK_ORDER_URL =
  'https://metabase.nudos.co/question/388-buscador-de-skus-para-cargue-de-herramientas?Modelo=&Procesador=&Memoria=&Almacenamiento=&SKU=';

export enum SHIPMENT_STATUS_STOCK_ORDER {
  CONFIRMED = 'Confirmada',
  DELIVERY_PROCESS = 'Proceso de entrega',
  DELIVERED = 'Entregada',
  DELAYED = 'Atrasada',
  CANCELLED = 'Cancelada'
}

export enum SHIPMENT_STATUS_ID_STOCK_ORDER {
  CONFIRMED = 1,
  DELIVERY_PROCESS = 2,
  DELIVERED = 3,
  DELAYED = 4,
  CANCELLED = 5
}

export const SHIPMENT_STATUS_OPTION_LIST_STOCK_ORDER = [
  { name: SHIPMENT_STATUS_STOCK_ORDER.CONFIRMED, ShipmentStatusId: SHIPMENT_STATUS_ID_STOCK_ORDER.CONFIRMED },
  {
    name: SHIPMENT_STATUS_STOCK_ORDER.DELIVERY_PROCESS,
    ShipmentStatusId: SHIPMENT_STATUS_ID_STOCK_ORDER.DELIVERY_PROCESS
  },
  { name: SHIPMENT_STATUS_STOCK_ORDER.DELIVERED, ShipmentStatusId: SHIPMENT_STATUS_ID_STOCK_ORDER.DELIVERED },
  { name: SHIPMENT_STATUS_STOCK_ORDER.CANCELLED, ShipmentStatusId: SHIPMENT_STATUS_ID_STOCK_ORDER.CANCELLED }
];

export type ICountry = IOrderCountry;

export interface IStockOrderShipmentFormatted {
  shipmentId: number;
  stockShipmentUuid: string;
  unit: number;
  products: IProductDelivery[];
  status: string;
  statusId: number;
  trackingInfo: ITrackingInfo;
  country: ICountry;
}

export enum ORDER_COMMENT_TYPES_ENUM_STOCK_ORDER {
  OPS_NOTE = 'orderOperationNote'
}

export interface IcreateStockOrderComment {
  commentType: ORDER_COMMENT_TYPES_ENUM_STOCK_ORDER;
  stockOrderId: number;
  userId: number;
  comments: string;
  mentions: number[];
  commentImage?: File;
}

export type IStockProductAvailabilityRequest = string[];

export interface ITotalStockInfo {
  quantity: number;
  stockProductsIds: number[];
}

export interface IAvailableStockInfoItem {
  stockProductId: number;
  serial: string;
  providerName: string;
  invoiceNumber: string;
  entryDate: string;
  stockOrderId: number | null;
  value?: number | null;
  saleId?: string | number | null;
}

export interface IAvailableStockInfo {
  quantity: number;
  items: IAvailableStockInfoItem[];
}

export interface IReservedStockInfoItem {
  orderId: number;
  quantity: number;
  stockProductsIds: number[];
}

export interface IReservedStockInfo {
  quantity: number;
  items: IReservedStockInfoItem[];
}

export interface IStockProductAvailabilityData {
  totalStockInfo: ITotalStockInfo;
  availableStockInfo: IAvailableStockInfo;
  reservedStockInfo: IReservedStockInfo;
  sku: string;
}

export interface ICreateReservationStockItem {
  stockProductId: number;
  saleId: string | number | null;
}

export type IDeleteReservationStockItem = number[];
export interface IStockReservation {
  stockOrderId: number;
  stockOrderUuid: string;
  stockProduct: IAvailableStockInfoItem;
}
